import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, UserCheck, UserX, Shield, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import endpoints from '../api/endpoints';
import '../assets/css/users.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    password: '',
    role: 'researcher',
    active: true
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get(endpoints.auth.users());
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      name: '',
      password: '',
      role: 'researcher',
      active: true
    });
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      active: user.active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password; // Don't send empty password
        
        await api.put(endpoints.auth.users(editingUser.id), updateData);
        toast.success('User updated successfully');
      } else {
        // Create new user
        await api.post(endpoints.auth.users(), formData);
        toast.success('User created successfully');
      }
      
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }
    
    try {
      await api.delete(endpoints.auth.users(userId));
      toast.success('User deactivated successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== 'admin') {
    return (
      <div className="user-container">
        <div className="user-card">
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-container">
      <div className="user-header">
        <h1 className="user-title">
          <Users size={32} />
          User Management
        </h1>
        <button className="user-btn-primary" onClick={handleAddUser}>
          <Plus size={20} />
          Add User
        </button>
      </div>

      <div className="user-card">
        <div className="user-searchbar">
          <Search size={20} color="#94a3b8" />
          <input
            className="user-search-input"
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading ? (
          <p>Loading users...</p>
        ) : (
          <table className="user-table">
            <thead>
              <tr>
                <th className="user-th">Name</th>
                <th className="user-th">Username</th>
                <th className="user-th">Email</th>
                <th className="user-th">Role</th>
                <th className="user-th">Status</th>
                <th className="user-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr className="user-row" key={u.id || u._id}>
                  <td className="user-td"><strong>{u.name}</strong></td>
                  <td className="user-td">{u.username}</td>
                  <td className="user-td">{u.email}</td>
                  <td className="user-td">
                    <span className={`user-badge ${u.role === 'admin' ? 'user-badge--admin' : ''}`}>
                      {u.role === 'admin' && <Shield size={12} />}
                      {u.role}
                    </span>
                  </td>
                  <td className="user-td">
                    <span className={`user-status-badge ${u.active ? 'user-status--active' : 'user-status--inactive'}`}>
                      {u.active ? <UserCheck size={12} /> : <UserX size={12} />}
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="user-td">
                    <div className="user-actions">
                      <button
                        className="user-icon-button"
                        onClick={() => handleEditUser(u)}
                        title="Edit user"
                      >
                        <Edit2 size={16} />
                      </button>
                      {u.id !== user.id && (
                        <button
                          className="user-icon-button user-icon-button--danger"
                          onClick={() => handleDeleteUser(u.id)}
                          title="Deactivate user"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="user-modal" onClick={() => setShowModal(false)}>
          <div className="user-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="user-modal-title">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h2>
            <form className="user-form" onSubmit={handleSubmit}>
              <div className="user-form-group">
                <label className="user-label">Full Name</label>
                <input
                  className="user-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="user-form-group">
                <label className="user-label">Username</label>
                <input
                  className="user-input"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>

              <div className="user-form-group">
                <label className="user-label">Email</label>
                <input
                  className="user-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="user-form-group">
                <label className="user-label">Password {editingUser && '(leave blank to keep current)'}</label>
                <input
                  className="user-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>

              <div className="user-form-group">
                <label className="user-label">Role</label>
                <select
                  className="user-select"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="researcher">Researcher</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>

              {editingUser && (
                <div className="user-form-group">
                  <label className="user-label">Status</label>
                  <select
                    className="user-select"
                    value={formData.active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value === 'active' })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}

              <div className="user-modal-buttons">
                <button className="user-btn-secondary" type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="user-btn-primary" type="submit">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

